import { IAppContext } from 'App/AppContext';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import Task, { TaskTypes } from 'models/tasks/task';
import { FeatureTypes, hasUserDataPermission, hasUserFeature } from 'services/Auth/featurePermissions';

// Task Auth Rules
// ---------------
// 1. Assignees are allowed to update all fields except:
//    a. Owner
//    b. Start date and time
//    c. Recurrence
//    .even without update permission in the sharing permissions.
// 2. Owners are allowed to read their tasks, even without an entry in the sharing permissions.
// 3. When a user has read-only permissions, due to:
//    a. Sharing permissions with update permission set to false
//    b. User is the assignee
//    -> Then, the user can assign the task to him/herself
// In addition, each field level function can hold additional business rules why a field can/cannot be updated
//

export enum TaskFieldTypes {
  Name = 1,
  Description = 2,
  Owner = 3,
  Tags = 4,
  Assignee = 5,
  Status = 6,
  Start = 7,
  Deadline = 8,
  Recurrence = 9,
  ChecklistEdit = 10,
  Context = 11,
  Permissions = 12,
  OutlookCreate = 13,
  OutlookRemove = 14,
  Library = 15,
  ChecklistFill = 16,
  AssignToMe = 17,
  Form = 18,
  ExternalId = 19,
}

export const canUpdateTasks = (tasks: Task[], appContext: IAppContext): boolean => {
  //returns true when the user can update all tasks
  return tasks.some(
    (task) =>
      task && //guard against undefined tasks
      (task.userId === appContext.user.id ||
        hasUserDataPermission(appContext, [task.authSchemaId], AuthSchemaLineOperation.Update)),
  );
};

export const canDeleteTasks = (tasks: Task[], appContext: IAppContext): boolean => {
  //returns true when the user can delete all tasks
  return tasks.some(
    (task) => task && hasUserDataPermission(appContext, [task.authSchemaId], AuthSchemaLineOperation.Delete),
  );
};

export const hasTaskUpdatePermission = (task: Task, appContext: IAppContext): boolean => {
  //returns true when the user has update permission according to the schema
  return hasUserDataPermission(appContext, [task.authSchemaId], AuthSchemaLineOperation.Update);
};

export const hasTaskDeletePermission = (task: Task, appContext: IAppContext): boolean => {
  //returns true when the user has delete permission according to the schema
  return hasUserDataPermission(appContext, [task.authSchemaId], AuthSchemaLineOperation.Delete);
};

export const canUpdateTaskField = (
  task: Task,
  canUpdateTask: boolean,
  taskFieldTypes: TaskFieldTypes,
  appContext: IAppContext,
): boolean => {
  // guard against undefined task
  if (!task) return false;
  // no restrictions on new tasks, except for recurrence: only managers can create recurring tasks
  if (task.taskId <= 0) {
    if (taskFieldTypes === TaskFieldTypes.Recurrence && !hasUserFeature(appContext, FeatureTypes.GenericManager)) {
      return false;
    }

    return true;
  }

  // when the task is of a template type, the user must be a manager to update any field
  if (
    task.taskType === TaskTypes.Template ||
    task.taskType === TaskTypes.EventTemplate ||
    (task.taskType === TaskTypes.Monitoring && task.isSeries())
  ) {
    if (!hasUserFeature(appContext, FeatureTypes.GenericManager)) {
      return false;
    }
  }

  //when the current user is the assignee, then the user has special permissions
  //same is true for events (no subtasks) when the user is the owner (because events cannot be assigned to a user)
  let isAssignee = false;
  if (task.taskType === TaskTypes.Event && task.taskMasterId === 0) {
    isAssignee = task.ownerId === appContext.user.id;
  } else {
    isAssignee = task.userId === appContext.user.id;
  }

  //instances of a series with 'only allow work from the start date' cannot be worked on before the start date
  if (
    (taskFieldTypes === TaskFieldTypes.Status ||
      taskFieldTypes === TaskFieldTypes.ChecklistFill ||
      taskFieldTypes === TaskFieldTypes.Form) &&
    task.isLockedUntilStart()
  ) {
    return false;
  }

  switch (taskFieldTypes) {
    case TaskFieldTypes.Name:
      if (task.taskType === TaskTypes.Normal && task.isInstance()) return false;
      if (isAssignee) return true;

      return canUpdateTask;
    case TaskFieldTypes.Description:
      if (task.taskType === TaskTypes.Normal && task.isInstance()) return false;
      if (isAssignee) return true;

      return canUpdateTask;
    case TaskFieldTypes.Owner:
      return canUpdateTask;
    case TaskFieldTypes.Tags:
      if (isAssignee) return true;

      return canUpdateTask;
    case TaskFieldTypes.Permissions:
      return canUpdateTask;
    case TaskFieldTypes.Assignee:
      if (isAssignee) return true;

      return canUpdateTask;
    case TaskFieldTypes.AssignToMe:
      if (task.authSchemaId !== undefined) {
        //when the task is shared with a team where you are a member, you can assign the task to yourself
        const schema = appContext.globalDataCache.authSchemas.get(task.authSchemaId);
        if (schema?.hasMember(appContext.user.id)) {
          return true;
        }
      }
      if (isAssignee) return true;

      return canUpdateTask;
    case TaskFieldTypes.Status:
      if (isAssignee) return true;

      return canUpdateTask;
    case TaskFieldTypes.Start:
      return canUpdateTask;
    case TaskFieldTypes.Deadline:
      if (task.taskType === TaskTypes.Normal && task.isInstance()) return false;
      if (isAssignee) return true;

      return canUpdateTask;
    case TaskFieldTypes.Recurrence:
      //it is never allowed to update recurrence for instances, this is controlled by the master task
      if (task.isInstance()) return false;

      return canUpdateTask;
    case TaskFieldTypes.ExternalId:
      //external id follows normal update permission and is editable for instances as well
      return canUpdateTask;
    case TaskFieldTypes.ChecklistFill:
      if (isAssignee) return true;

      return canUpdateTask;
    case TaskFieldTypes.ChecklistEdit:
      if (task.taskType === TaskTypes.Normal && task.isInstance()) return false;
      if (isAssignee) return true;

      return canUpdateTask;
    case TaskFieldTypes.Context:
      if (isAssignee) return true;

      return canUpdateTask;
    case TaskFieldTypes.Library:
      if (isAssignee) return true;

      return canUpdateTask;
    case TaskFieldTypes.Form:
      if (isAssignee) return true;

      return canUpdateTask;
    case TaskFieldTypes.OutlookCreate:
      if (!task.isAssigned()) return false;
      if (isAssignee) return true;

      return canUpdateTask;
    case TaskFieldTypes.OutlookRemove:
      if (!task.isScheduled()) return false;
      if (isAssignee) return true;

      return canUpdateTask;
    default:
      return true;
  }
};
